import type { OnRequestConfigDto, PluralTranslator } from "@tripcart/shared";

import type { PartyTotals } from "@/lib/party-totals";

import styles from "./sticky-book-bar.module.css";

/** The left half of the bar on an on-request tour (AC-11) — there is no
 *  date-based price to total, so this replaces `BarPrice` entirely rather
 *  than adding a fourth branch to it: the operator's own estimated price when
 *  they chose to show one, else a neutral line naming what the tap does. */
export function BarQuotePrice({
  onRequest,
  formatMoney,
  t,
}: {
  onRequest: OnRequestConfigDto;
  formatMoney: (minor: number) => string;
  t: (key: string) => string;
}) {
  if (!onRequest.showEstimatedPrice || !onRequest.estimatedFrom) {
    return <span className={styles.status}>{t("Request a custom quote")}</span>;
  }
  const was =
    onRequest.originalPrice &&
    onRequest.originalPrice.amount > onRequest.estimatedFrom.amount
      ? formatMoney(onRequest.originalPrice.amount)
      : null;
  return (
    <BarFigure
      label={onRequest.priceBeforeText || t("Estimated From")}
      figure={formatMoney(onRequest.estimatedFrom.amount)}
      was={was}
      suffix={onRequest.priceAfterText || undefined}
    />
  );
}

/** The left half of the bar: what this tour costs, or why it cannot say.
 *
 *  Its own component because the bar's one function grew past the 60-line cap
 *  when the was-price and the per-line arrived — and because the three states
 *  here (not configured / a price / no price yet) are the only branching the
 *  bar does.
 */
export function BarPrice({
  notConfigured,
  priceLabel,
  wasLabel,
  priceSuffix,
  party,
  formatMoney,
  t,
  tn,
}: {
  notConfigured: boolean;
  priceLabel: string | null;
  wasLabel: string | null;
  priceSuffix?: string;
  party: PartyTotals | null;
  formatMoney: (minor: number) => string;
  t: (key: string) => string;
  tn: PluralTranslator;
}) {
  if (notConfigured) {
    return <span className={styles.status}>{t("Booking Opens Soon")}</span>;
  }
  if (!priceLabel) {
    return (
      <span className={styles.status}>{t("Pick your date to see prices")}</span>
    );
  }
  // The rail's own figure, when it has one — see `PartyFigure`.
  if (party)
    return (
      <PartyFigure
        party={party}
        suffix={priceSuffix}
        formatMoney={formatMoney}
        t={t}
        tn={tn}
      />
    );

  // No priced category to total — the operator's headline figure, with the
  // was-price and per-line they set beside it.
  return (
    <BarFigure
      label={t("From")}
      figure={priceLabel}
      was={wasLabel}
      suffix={priceSuffix}
    />
  );
}

/**
 * What the booking rail is charging, shown in the bar.
 *
 * Preferred over the from-price because it is what the traveller is actually
 * being asked for, and because the rail is showing the same number a few
 * thousand pixels down the page — two different figures for one selection is
 * the thing to avoid.
 *
 * Its own component so `BarPrice` stays inside the 60-line cap.
 */
function PartyFigure({
  party,
  suffix,
  formatMoney,
  t,
  tn,
}: {
  party: PartyTotals;
  /** The operator's per-line. Carried on the "Starting from" branch only — see
   *  `priceSuffix` on the props above for why a party total must not take it. */
  suffix?: string;
  formatMoney: (minor: number) => string;
  t: (key: string) => string;
  tn: PluralTranslator;
}) {
  // Struck through only when genuinely higher. Equal figures side by side, one
  // crossed out, is a discount claim that is not true.
  const wasTotal =
    party.originalTotalMinor > party.totalMinor
      ? formatMoney(party.originalTotalMinor)
      : null;
  return (
    <BarFigure
      // THE RAIL'S OWN WORDS, verbatim — `#railLabel` in `_booking-sidebar`.
      // The bar and the rail show the same number a few thousand pixels apart,
      // so they say the same thing about it; reusing the strings also means no
      // new catalog entries for every locale.
      //
      // The head count lives in the LABEL rather than beside the figure, which
      // is where the guide puts it ("$3,190 $3,544 2 travellers"). At 390px a
      // currency like `₨1,450.00` next to a struck `₨1,690.00` has already
      // filled that row, and the count wrapped to a third line and grew the bar.
      // The guide's own figures are four characters shorter.
      label={
        party.isPartyTotal
          ? tn(
              "Total price for 1 traveler",
              "Total price for {n} travelers",
              party.headCount,
              { n: party.headCount },
            )
          : t("Starting from")
      }
      figure={formatMoney(party.totalMinor)}
      was={wasTotal}
      // Only under "Starting from". That branch fires at a single head, so the
      // figure is one person's price and the operator's "per person" qualifies
      // it exactly; beside a party total the same words would be false.
      suffix={party.isPartyTotal ? undefined : suffix}
    />
  );
}

/**
 * The bar's price row: a label over a figure, with an optional struck-through
 * was-price and an optional qualifier beside it.
 *
 * One component for both branches so "Total / ₨3,190 / 2 travellers" and
 * "From / ₨1,090 / Adult" can never drift apart in size, weight or spacing —
 * they are the same row answering two different questions.
 *
 * Guide `.bb-from` over `.bb-amt`: the label LEADS and the figure sits under
 * it. This was the other way round, because on a 56px bar the overline spent
 * the top third of the only line anyone reads on the word "From". At 64px both
 * fit, and label-first is what the mobile guide draws — it also leaves the
 * figure sharing one baseline with the was-price and the qualifier, which is
 * the row a traveller actually scans.
 */
function BarFigure({
  label,
  figure,
  was,
  suffix,
}: {
  label: string;
  figure: string;
  was: string | null;
  suffix?: string;
}) {
  return (
    <div className={styles.price}>
      <span className={styles.priceLabel}>{label}</span>
      <span className={styles.priceRow}>
        <span className={styles.figure}>{figure}</span>
        {was ? <s className={styles.was}>{was}</s> : null}
        {suffix ? <span className={styles.suffix}>{suffix}</span> : null}
      </span>
    </div>
  );
}
